"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "refrigerator-size-calculator",
  "kitchen-space-planner",
  "energy-cost-calculator",
  "refrigerator-coil-cleaning-savings-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Noise Level Calculator",
  category: "Comparison" as const,
  mediumDescription:
    "Compare refrigerator decibel ratings to common household sounds and decide which rooms a unit fits in. Reference points come from EPA noise guidance and ASHRAE acoustic standards.",
  faqs: [
    { question: "What's a quiet refrigerator dB rating?", answer: "Modern fridges range from 32 to 50 dB. Anything under 38 dB is considered very quiet — comparable to a quiet library. Most premium counter-depth and built-in models target 38–42 dB." },
    { question: "Will a 45 dB fridge be too loud for an open kitchen?", answer: "45 dB is comparable to a quiet office and below normal conversation (60 dB). Most homeowners don't notice a 45 dB fridge in an active kitchen, but it's audible during quiet evenings." },
    { question: "What does the dB scale actually mean?", answer: "Decibels are logarithmic — every 10 dB increase represents roughly twice the perceived loudness. A 50 dB fridge sounds about twice as loud as a 40 dB one, even though it's only 25% higher numerically." },
    { question: "Is the manufacturer's dB rating accurate?", answer: "Most ratings are measured at 1 meter in an anechoic chamber per IEC 60704. Real-world levels in a kitchen with hard floors and tile can be 2–5 dB higher due to reflection." },
    { question: "What's the EPA-recommended residential noise limit?", answer: "EPA's Levels Document (1974, still cited) recommends interior residential noise stay below 45 dB Ldn for sleep and concentration. Bedrooms should aim for 30–35 dB ambient." },
    { question: "Why does my old fridge sound louder than the new one?", answer: "Single-speed compressors on/off-cycle, creating noticeable noise transitions. Modern inverter compressors run at variable speeds — quieter and more constant. Worn compressor mounts also add vibration noise." },
  ],
  relatedTools: ["refrigerator-size-calculator", "kitchen-space-planner", "refrigerator-coil-cleaning-savings-calculator"],
}

export function NoiseLevelCalculatorPageClient() {
  const [decibels, setDecibels] = useState("42")
  const [location, setLocation] = useState("kitchen")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // 20 dB is the quietest level an anechoic measurement resolves; no appliance
    // is rated below it, and nothing near a fridge reaches 100.
    const db = readQuantity(decibels, { min: 20, max: 100 })
    if (db === null) return null

    let rating = ""
    let comparison = ""
    if (db < 30) {
      rating = "Whisper quiet"
      comparison = "Quieter than a whisper (30 dB)"
    } else if (db < 40) {
      rating = "Very quiet"
      comparison = "Like a quiet library (30–40 dB)"
    } else if (db < 45) {
      rating = "Quiet"
      comparison = "Like a quiet office or refrigerator hum (40–45 dB)"
    } else if (db < 50) {
      rating = "Moderate"
      comparison = "Like normal conversation at 5 feet (45–50 dB)"
    } else if (db < 60) {
      rating = "Noticeable"
      comparison = "Like background music or AC unit (50–60 dB)"
    } else {
      rating = "Loud"
      comparison = "Like a dishwasher or vacuum (60+ dB)"
    }

    const suitability = [
      { location: "Open kitchen", suitable: db < 50, reason: db < 50 ? "Acceptable — won't interfere with conversation." : "May be noticeable during quiet moments." },
      { location: "Bedroom", suitable: db < 40, reason: db < 40 ? "Quiet enough for sleeping." : "May disturb sleep — EPA recommends bedrooms under 35 dB." },
      { location: "Office / study", suitable: db < 45, reason: db < 45 ? "Won't interfere with concentration." : "May distract during focused work." },
      { location: "Living room", suitable: db < 50, reason: db < 50 ? "Acceptable background noise." : "May interfere with TV or conversation." },
      { location: "Basement / garage", suitable: db < 60, reason: "Acceptable for utility spaces." },
    ]

    const selected = suitability.find((s) => s.location.toLowerCase().includes(location)) || suitability[0]

    return { decibelLevel: db, rating, comparison, suitability, selected }
  }, [decibels, location])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Will that fridge"
      heroTitleItalic="actually"
      heroTitlePost="be quiet enough?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "EPA + IEC 60704 references"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "32–50", label: "dB range", note: "Modern fridges" },
            { value: "<38", label: "Very quiet", note: "Library-quiet" },
            { value: "45 dB", label: "EPA limit", note: "Residential interior" },
            { value: "10 dB", label: "= 2× loudness", note: "Logarithmic scale" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-noise-level-calculator")}
      cta={{
        title: "Want the quietest fridges? Shop counter-depth.",
        body: "Browse counter-depth refrigerators at Fridge.com — most run 38–42 dB with smooth-start inverter compressors and reinforced cabinets.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Two inputs.
                <br />
                Real room fit.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Enter the manufacturer&apos;s dB rating; we benchmark it against EPA noise guidance for each room type.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Decibel rating
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={20}
                      max={100}
                      step="0.1"
                      value={decibels}
                      onChange={(e) => setDecibels(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">dB</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Find on the fridge spec sheet — usually labeled &quot;Sound Level&quot;.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Intended room
                  </label>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="kitchen">Open kitchen</option>
                    <option value="bedroom">Bedroom</option>
                    <option value="office">Office / study</option>
                    <option value="living">Living room</option>
                    <option value="basement">Basement</option>
                    <option value="garage">Garage</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Loudness rating
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(48px, 6vw, 80px)", letterSpacing: "-0.045em", lineHeight: 0.95 }}>
                    {result?.rating ?? "—"}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.comparison ?? "Enter a dB rating."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Selected room</div>
                      <div className="text-[14px] sm:text-[16px] font-black tracking-tight">{result?.selected?.location ?? "—"}</div>
                      <div className="text-[11px] text-white/70 mt-1 leading-[1.4]">{result?.selected?.reason ?? ""}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Quietest fit</div>
                      <div className="text-[14px] sm:text-[16px] font-black tracking-tight">
                        {result && result.decibelLevel < 40 ? "All rooms" : result && result.decibelLevel < 45 ? "Living + kitchen" : "Kitchen / utility"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Quiet by design"
          title="Counter-depth fridges"
          description="Reinforced cabinets and inverter compressors. Most premium models post 38–42 dB ratings."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">EPA</em> guidance. IEC method.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Bands map to perceived loudness ranges in the EPA Levels Document (1974). Manufacturer dB ratings follow IEC 60704 (1 m, anechoic chamber).
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Loudness bands", d: "<30: whisper. 30–40: very quiet. 40–45: quiet. 45–50: moderate. 50–60: noticeable. 60+: loud." },
                { n: "02", t: "Room thresholds", d: "Bedroom: <40 dB (EPA recommends sleep <35). Office: <45. Living: <50. Kitchen: <50. Utility: <60." },
                { n: "03", t: "Logarithmic perception", d: "Each +10 dB ≈ 2× perceived loudness. A 50 dB fridge sounds twice as loud as 40 dB, even though it's only 25% higher numerically." },
                { n: "04", t: "Real-world adjustment", d: "Hard floors and tile add 2–5 dB to the spec sheet number. Soft cabinets and rugs absorb. Plan accordingly." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges"
          description="Premium French door builds typically post 41–44 dB — quiet enough for open kitchens and great-rooms."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
